import React, { useEffect, useRef, useState } from 'react';
import {
  TextField,
  Button,
  Avatar,
  Box,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Upload as UploadIcon,
  Camera as CameraIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  DocumentScanner as LirePasseportIcon,
  Undo as AnnulerIcon
} from '@mui/icons-material';
import {
  recupererInfoVoyageurLegrand,
  modifierInfoVoyageurLegrand,
  recupererPasseportLegrand,
  uploadVisaLegrand,
  majDemandeLegrand,
  envoyerMailVisaTraiteLegrand,
  uploadPasseportLegrand,
  uploadPhotoVoyageurLegrand,
  uploadEcardLegrand,
  recupererContenuFormulaireLegrand,
  recupererEcardLegrand
} from '../../services/legrandServices';
import { Voyageurs } from '../../models/Voyageurs';
import useIsMobile from '../../hooks/useIsMobile';
import { FILES_BASE_URL } from '../../api/config';
import {
  lirePasseport,
  appliquerLecture,
  resumeLecture,
  texteMarque,
  couleurMarque,
  messageErreurLecture,
  ChampLu,
  MarquesLecture,
} from '../../services/lecturePasseportServices';

interface InfoVoyageurLegrandProps {
  idVoyageur: number;
  chemin: string;
  pays: string;
  idDemande: number;
  onValider: () => void;
}

const InfoVoyageurLegrand: React.FC<InfoVoyageurLegrandProps> = ({
  idVoyageur,
  chemin,
  pays,
  idDemande,
  onValider
}) => {
  const [voyageur, setVoyageur] = useState<Voyageurs | null>(null);
  const [urlPasseport, setUrlPasseport] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [passeportType, setPasseportType] = useState<'pdf' | 'image' | null>(null);
  const [photoTimestamp, setPhotoTimestamp] = useState<number>(Date.now());

  // État pour la modale eVisa
  const [openModalEVisa, setOpenModalEVisa] = useState(false);
  const [dateValiditeEVisa, setDateValiditeEVisa] = useState('');
  const [dateExpirationEVisa, setDateExpirationEVisa] = useState('');
  const [infosVisaEVisa, setInfosVisaEVisa] = useState('');
  const [emailSubjectEVisa, setEmailSubjectEVisa] = useState('');
  const [emailContentEVisa, setEmailContentEVisa] = useState('');
  const [sendEmailEVisa, setSendEmailEVisa] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ecardInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  // État pour la modale visa classique
  const [openModalVisaClassique, setOpenModalVisaClassique] = useState(false);
  const [dateValiditeVC, setDateValiditeVC] = useState('');
  const [dateExpirationVC, setDateExpirationVC] = useState('');
  const [infosVisaVC, setInfosVisaVC] = useState('');
  const [emailSubjectVC, setEmailSubjectVC] = useState('');
  const [emailContentVC, setEmailContentVC] = useState('');
  const [sendEmailVC, setSendEmailVC] = useState(true);

  // Refs pour upload passeport et photo
  const passeportInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Récupérer le rôle de l'utilisateur
  const [userRole, setUserRole] = useState<string>('admin');

  // Détermine si c'est un eVisa
  const isEVisa = pays.toLowerCase().includes('evisa');

  // Détermine si c'est un visa Inde eVisa
  const isIndeEVisa = pays.toLowerCase().includes('inde') && pays.toLowerCase().includes('evisa');

  // Détermine si c'est un visa Togo eVisa
  const isTogoEVisa = pays.toLowerCase().includes('togo') && pays.toLowerCase().includes('evisa');

  // Détermine si c'est un pays avec eCard (carte d'arrivée)
  const isECardPays = pays.toLowerCase().includes('ecard');

  // Détermine si c'est un visa Chine (avec ou sans accompagnement)
  const isChine = pays.toLowerCase().includes('chine');

  // Indique si le client Chine a choisi de nous confier la carte d'arrivée (lu depuis json.txt)
  const [chineWantsECard, setChineWantsECard] = useState<boolean>(false);

  // URL de la carte d'arrivée (e-card) si elle a déjà été uploadée
  const [urlEcard, setUrlEcard] = useState<string | null>(null);

  // États pour la modale e-card
  const [openModalECard, setOpenModalECard] = useState(false);
  const [emailSubjectECard, setEmailSubjectECard] = useState('');
  const [emailContentECard, setEmailContentECard] = useState('');
  const [sendEmailECard, setSendEmailECard] = useState(true);

  // Bouton « Lire le passeport » (superadmin, comme « Valider les modifications ») : valeurs lues sur le passeport
  // déposé, proposées dans la fiche (marquées en vert si contrôlées, en orange si à vérifier) ; rien n'est enregistré
  // avant « Valider les modifications »
  const [lectureEnCours, setLectureEnCours] = useState(false);
  const [erreurLecture, setErreurLecture] = useState<string | null>(null);
  const [lecture, setLecture] = useState<{
    marques: MarquesLecture;
    avant: Voyageurs;        // fiche d'avant la lecture (« Annuler la lecture »)
    resume: string;
    avertissements: string[];
    aVerifier: boolean;
  } | null>(null);

  useEffect(() => {
    setLecture(null);
    setErreurLecture(null);
    // Récupérer le rôle de l'utilisateur
    const userData = localStorage.getItem('user_legrand');
    if (userData) {
      const user = JSON.parse(userData);
      setUserRole(user.role || 'admin');
    }

    const fetchVoyageur = async () => {
      try {
        const infoVoyageur = await recupererInfoVoyageurLegrand(idVoyageur);
        setVoyageur(infoVoyageur);
        if (infoVoyageur.url) {
          setPhotoUrl(`${infoVoyageur.url}?t=${photoTimestamp}`);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des informations du voyageur:', error);
      }
    };

    if (idVoyageur && idVoyageur > 0) {
      fetchVoyageur();
    }
  }, [idVoyageur]);

  // Numéro et dates jamais null dans les champs. Seulement s'il en manque un : sans cette condition, chaque rendu
  // recréait la fiche et relançait cet effet sans fin (rendus en boucle)
  useEffect(() => {
    if (voyageur && [voyageur.numeropax, voyageur.datedelivrance, voyageur.dateexpiration, voyageur.datenaissance]
      .some((v) => v === null || v === undefined)) {
      setVoyageur({
        ...voyageur,
        numeropax: voyageur.numeropax || '',
        datedelivrance: voyageur.datedelivrance || '',
        dateexpiration: voyageur.dateexpiration || '',
        datenaissance: voyageur.datenaissance || '',
      });
    }
  }, [voyageur]);

  // Pour la Chine, on lit le json.txt pour savoir si le client a coché "carte d'arrivée = oui"
  useEffect(() => {
    if (isChine && chemin) {
      recupererContenuFormulaireLegrand(chemin)
        .then((donnees: any) => {
          if (donnees && donnees.carteArrivee === 'oui') {
            setChineWantsECard(true);
          } else {
            setChineWantsECard(false);
          }
        })
        .catch(() => setChineWantsECard(false));
    }
  }, [isChine, chemin]);

  // Charge l'URL de la carte d'arrivée (e-card) si déjà uploadée pour cette demande
  useEffect(() => {
    const needs = isIndeEVisa || isTogoEVisa || isECardPays || (isChine && chineWantsECard);
    if (chemin && needs) {
      recupererEcardLegrand(chemin)
        .then((url) => setUrlEcard(url))
        .catch(() => setUrlEcard(null));
    } else {
      setUrlEcard(null);
    }
  }, [chemin, isIndeEVisa, isTogoEVisa, isECardPays, isChine, chineWantsECard]);

  useEffect(() => {
    const fetchPasseport = async () => {
      if (voyageur && voyageur.dossier) {
        try {
          const url = await recupererPasseportLegrand(voyageur.dossier);
          setUrlPasseport(url);

          // Déterminer le type de fichier
          if (url) {
            const extension = url.split('.').pop()?.toLowerCase();
            if (extension === 'pdf') {
              setPasseportType('pdf');
            } else if (['jpg', 'jpeg', 'png'].includes(extension || '')) {
              setPasseportType('image');
            }
          }
        } catch (error) {
          console.error("Erreur lors de la récupération de l'URL du passeport:", error);
        }
      }
    };

    if (voyageur?.dossier) {
      fetchPasseport();
    }
  }, [voyageur?.dossier]);

  // Champ corrigé à la main après une lecture : sa marque (vert / orange) n'a plus lieu d'être
  const oublierMarque = (name: string) => {
    setLecture((l) => {
      if (!l || !l.marques[name as ChampLu]) return l;
      const marques = { ...l.marques };
      delete marques[name as ChampLu];
      return { ...l, marques };
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    oublierMarque(name);
    setVoyageur((prev) => ({
      ...prev!,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!voyageur) return;
    try {
      await modifierInfoVoyageurLegrand(voyageur);
      setLecture(null);
      alert("Informations mises à jour avec succès !");
      onValider();
    } catch (error) {
      console.error('Erreur lors de la mise à jour des informations du voyageur:', error);
    }
  };

  // Lecture du passeport déposé (OCR sur le serveur admin, même route que l'admin VisaMonde : même table voyageurs,
  // même dossier) -> champs pré-remplis dans la fiche, non enregistrés
  const handleLirePasseport = async () => {
    if (!voyageur) return;
    setLectureEnCours(true);
    setErreurLecture(null);
    try {
      const r = await lirePasseport(idVoyageur);
      if (!r.ok) {
        setErreurLecture(r.message);
        return;
      }
      const applique = appliquerLecture(voyageur, r);
      setVoyageur(applique.voyageur);
      setLecture({
        marques: applique.marques,
        avant: lecture ? lecture.avant : voyageur,
        resume: resumeLecture(applique, 'Valider les modifications'),
        avertissements: r.avertissements,
        aVerifier: applique.nbAVerifier > 0 || applique.nbNonRepris > 0 || r.avertissements.length > 0,
      });
    } catch (error) {
      setErreurLecture(messageErreurLecture(error));
    } finally {
      setLectureEnCours(false);
    }
  };

  const handleAnnulerLecture = () => {
    if (lecture) setVoyageur(lecture.avant);
    setLecture(null);
  };

  // Champ marqué par la lecture : texte sous le champ et bordure (vert contrôlé, orange à vérifier, bleu non repris)
  const marque = (champ: ChampLu) => {
    const m = lecture ? lecture.marques[champ] : undefined;
    if (!m) return {};
    const couleur = couleurMarque(m);
    return {
      helperText: texteMarque(m),
      FormHelperTextProps: { sx: { color: `${couleur}.main` } },
      sx: { '& .MuiOutlinedInput-notchedOutline': { borderColor: `${couleur}.main`, borderWidth: m.etat === 'verifier' ? 2 : 1 } },
    };
  };

  // Handler pour upload de passeport
  const handlePasseportUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !voyageur?.dossier) return;

    const formData = new FormData();
    formData.append('passeport', file);

    try {
      await uploadPasseportLegrand(voyageur.dossier, formData);
      alert('Passeport uploadé avec succès');

      // Attendre un peu pour que le fichier soit bien écrit
      await new Promise(resolve => setTimeout(resolve, 500));

      // Rafraîchir l'URL du passeport
      const newUrl = await recupererPasseportLegrand(voyageur.dossier);
      if (newUrl) {
        setUrlPasseport(newUrl);

        // Mettre à jour le type de passeport
        const extension = newUrl.split('.').pop()?.toLowerCase();
        if (extension === 'pdf') {
          setPasseportType('pdf');
        } else if (['jpg', 'jpeg', 'png'].includes(extension || '')) {
          setPasseportType('image');
        }
      }
    } catch (error) {
      console.error("Erreur lors de l'upload du passeport:", error);
      alert("Erreur lors de l'upload du passeport");
    }
  };

  // Handler pour upload de photo
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !voyageur) return;

    const formData = new FormData();
    formData.append('photo', file);

    try {
      await uploadPhotoVoyageurLegrand(idVoyageur, formData);
      alert('Photo uploadée avec succès');

      // Forcer le rechargement avec un nouveau timestamp
      const newTimestamp = Date.now();
      setPhotoTimestamp(newTimestamp);

      // Recharger les infos du voyageur
      const updatedVoyageur = await recupererInfoVoyageurLegrand(idVoyageur);
      if (updatedVoyageur.url) {
        setPhotoUrl(`${updatedVoyageur.url}?t=${newTimestamp}`);
        setVoyageur(updatedVoyageur);
      }
    } catch (error) {
      console.error("Erreur lors de l'upload de la photo:", error);
      alert("Erreur lors de l'upload de la photo");
    }
  };

  // Gestion du eVisa
  const handleUploadClick = () => {
    // Récupérer nom et prénom du voyageur
    const nom = voyageur?.nompax || '';
    const prenom = voyageur?.prenompax || '';

    // Template de mail par défaut pour eVisa
    setEmailSubjectEVisa(`${nom} ${prenom} - Dossier (${idDemande}) - Votre visa est prêt`);

    // Vérifier si c'est un eVisa Inde pour utiliser le bon template
    const isIndeEVisaMail = pays.toLowerCase().includes('inde') && pays.toLowerCase().includes('evisa');

    // Générer l'URL du visa
    const urlVisa = `${FILES_BASE_URL}/demandes/${chemin}/visa.pdf`;

    if (isIndeEVisaMail) {
      setEmailContentEVisa(
        `Bonjour ${prenom},\n\n` +
        `Votre visa vient d'être délivré par les services consulaires, il est disponible dans le lien ci-dessous.\n\n` +
        `<div style="text-align: center; margin: 20px 0;">
          <a href="${urlVisa}" style="background-color: #2196F3; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold; font-size: 16px;">
            ✈️ Télécharger votre visa
          </a>
        </div><br><br>` +
        `Afin de voyager en toute tranquillité, nous vous invitons à l'imprimer pour être en mesure de le présenter en douane.\n\n` +
        `A savoir que votre visa est accessible à tout moment dans votre espace client dans l'onglet "mes visas".\n\n` +
        `Une e-Arrival card est nécessaire pour chaque entrée en Inde en plus du visa électronique. Elle s'obtient à moins de 5 jours du départ de manière électronique. Nos services se chargeront de son obtention sans surcoût pour le premier voyage effectué avec le visa délivré par notre équipe. Elle vous parviendra directement par mail et sera disponible à tout moment dans votre espace client dans l'onglet Mes Visas.\n` +
        `Si d'autres voyages sont prévus avec le même visa, une commande spéciale e-Arrival Card sera disponible dans votre espace client.\n\n\n` +
        `Toute l'équipe de Visa Monde vous remercie pour votre confiance et vous souhaite un agréable voyage.\n` +
        `Pour toute question, contactez-nous sur contact@visamonde.com\n\n` +
        `Cordialement,\n` +
        `L'équipe Visa Monde\n\n` +
        `<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">\n\n` +
        `https://visamonde.com\n` +
        `89 boulevard national\n` +
        `92250 LA GARENNE COLOMBES\n` +
        `(+33)1 86 22 08 06\n` +
        `contact@visamonde.com`
      );
    } else {
      setEmailContentEVisa(
        `Bonjour ${prenom},\n\n` +
        `Votre visa vient d'être délivré par les services consulaires, il est disponible dans le lien ci-dessous.\n\n` +
        `<div style="text-align: center; margin: 20px 0;">
          <a href="${urlVisa}" style="background-color: #2196F3; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold; font-size: 16px;">
            ✈️ Télécharger votre visa
          </a>
        </div><br><br>` +
        `Afin de voyager en toute tranquillité, nous vous invitons à l'imprimer pour être en mesure de le présenter en douane.\n\n` +
        `A savoir que votre visa est accessible à tout moment dans votre espace client dans l'onglet "mes visas".\n\n` +
        `Toute l'équipe de Visa Monde vous remercie pour votre confiance et vous souhaite un agréable voyage.\n` +
        `Pour toute question, contactez-nous sur contact@visamonde.com\n\n` +
        `Cordialement,\n` +
        `L'équipe Visa Monde\n\n` +
        `<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">\n\n` +
        `https://visamonde.com\n` +
        `89 boulevard national\n` +
        `92250 LA GARENNE COLOMBES\n` +
        `(+33)1 86 22 08 06\n` +
        `contact@visamonde.com`
      );
    }

    setSendEmailEVisa(true);
    setOpenModalEVisa(true);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("visa", file);
    formData.append("pays", pays);
    formData.append("datevalidite", dateValiditeEVisa);
    formData.append("dateexpiration", dateExpirationEVisa);
    formData.append("infosvisa", infosVisaEVisa);
    formData.append("sendEmail", sendEmailEVisa.toString());
    formData.append("subjectMail", emailSubjectEVisa);
    formData.append("mailContent", emailContentEVisa);

    try {
      // Upload du fichier avec toutes les informations
      await uploadVisaLegrand(chemin, formData);

      alert('Le visa a été uploadé avec succès.');
      onValider();
    } catch (error) {
      console.error("Erreur lors de l'upload du visa:", error);
      alert("Erreur lors de l'upload du visa.");
    }
  };

  const handleModalSubmitEVisa = () => {
    setOpenModalEVisa(false);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Gestion du visa classique avec email personnalisé pour Legrand
  const handleVisaClassiqueClick = () => {
    // Email par défaut personnalisé pour Legrand
    setEmailSubjectVC("LEGRAND - Votre visa est prêt");
    setEmailContentVC(
      `Bonjour,\n\n` +
      `Bonne nouvelle, votre visa pour votre déplacement professionnel LEGRAND vient d'être délivré par les services consulaires.\n` +
      `Votre passeport sera retourné au plus vite selon les modalités convenues avec votre service voyages LEGRAND.\n\n` +
      `Note : Ce visa a été traité dans le cadre de votre déplacement professionnel pour LEGRAND.\n\n` +
      `Pour toute question, contactez-nous sur contact@visamonde.com ou votre service voyages LEGRAND.\n\n` +
      `Cordialement,\n` +
      `L'équipe Visa Monde - Service Entreprise LEGRAND\n\n` +
      `<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">\n\n` +
      `https://visamonde.com\n` +
      `89 boulevard national\n` +
      `92250 LA GARENNE COLOMBES\n` +
      `(+33)1 86 22 08 06\n` +
      `contact@visamonde.com`
    );
    setOpenModalVisaClassique(true);
  };

  const handleModalSubmitVisaClassique = async () => {
    try {
      await envoyerMailVisaTraiteLegrand(chemin, {
        pays,
        datevalidite: dateValiditeVC,
        dateexpiration: dateExpirationVC,
        infosvisa: infosVisaVC,
        subjectMail: emailSubjectVC,
        mailContent: emailContentVC,
        sendEmail: sendEmailVC.toString()
      });

      const message = sendEmailVC
        ? 'Visa marqué comme traité et mail envoyé avec succès.'
        : 'Visa marqué comme traité (sans envoi de mail).';
      alert(message);
      setOpenModalVisaClassique(false);
      onValider();
    } catch (error) {
      console.error("Erreur lors de l'envoi du mail visa traité:", error);
      alert("Erreur lors de l'envoi du mail.");
    }
  };

  // --------------------------------
  // Gestion de l'E-Card pour Inde eVisa et Togo eVisa
  // --------------------------------
  const handleUploadECardClick = () => {
    // Récupérer nom et prénom du voyageur
    const nom = voyageur?.nompax || voyageur?.nom || '';
    const prenom = voyageur?.prenompax || voyageur?.prenom || '';

    // Générer l'URL de l'e-card
    const urlECard = `${FILES_BASE_URL}/demandes/${chemin}/ecard.pdf`;

    // Template de mail par défaut pour e-card
    if (isTogoEVisa) {
      setEmailSubjectECard(`${nom} ${prenom} - Dossier (${idDemande}) - Bordereau de sortie du Togo`);
      setEmailContentECard(
        `Bonjour,<br><br>` +
        `Votre bordereau de sortie du Togo vient d'être délivré par les services consulaires, il est disponible dans le lien ci-dessous.<br><br>` +
        `<div style="text-align: center; margin: 20px 0;">
          <a href="${urlECard}" style="background-color: #4CAF50; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold; font-size: 16px;">
            Cliquez ici pour accéder à votre bordereau
          </a>
        </div><br>` +
        `Afin de voyager en toute tranquillité, nous vous invitons à l'imprimer pour être en mesure de le présenter en douane.<br><br>` +
        `A savoir que votre bordereau est accessible à tout moment dans votre espace client.<br><br>` +
        `Toute l'équipe de Visa Monde vous remercie pour votre confiance et vous souhaite un agréable voyage.<br>` +
        `Pour toute question, contactez-nous sur contact@visamonde.com<br><br>` +
        `Cordialement,<br>` +
        `L'équipe Visa Monde<br>` +
        `https://visamonde.com<br>` +
        `89 boulevard national 92250 LA GARENNE COLOMBES (FRANCE)<br>` +
        `(+33)1 86 22 08 06<br>` +
        `contact@visamonde.com`
      );
    } else if (isECardPays || (isChine && chineWantsECard)) {
      // Template pour pays avec eCard (carte d'arrivée) — réutilisé aussi pour Chine
      setEmailSubjectECard(`${nom} ${prenom} – Dossier n°${idDemande} – Votre carte d'arrivée est prête 🎉`);
      setEmailContentECard(
        `Bonjour ${prenom}, 👋<br><br>` +
        `Excellente nouvelle : votre carte d'arrivée vient d'être délivrée par les services consulaires 🎉<br><br>` +
        `Vous pouvez dès à présent y accéder grâce au lien ci-dessous :<br><br>` +
        `<div style="text-align: center; margin: 20px 0;">
          <a href="${urlECard}" style="background-color: #4CAF50; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold; font-size: 16px;">
            Accéder à ma carte d'arrivée
          </a>
        </div><br>` +
        `📌 <strong>Conseils pour votre voyage :</strong><br>` +
        `Nous vous recommandons d'imprimer votre carte d'arrivée afin de pouvoir la présenter facilement lors des contrôles aux frontières ✈️🛂<br><br>` +
        `Votre carte d'arrivée reste disponible à tout moment depuis votre espace personnel, dans l'onglet « Mes visas » 📄✨<br><br>` +
        `Toute l'équipe Visa Monde vous remercie pour votre confiance<br>` +
        `et vous souhaite un excellent voyage 🌍✈️<br><br>` +
        `📩 Une question ?<br>` +
        `Notre équipe reste disponible pour vous accompagner.<br>` +
        `📧 contact@visamonde.com<br><br>` +
        `À très bientôt,<br>` +
        `L'équipe Visa Monde 🌍✈️<br>` +
        `📍 89 boulevard National – 92250 La Garenne-Colombes – France<br>` +
        `📞 (+33)1 86 22 08 06<br>` +
        `💻 https://visamonde.com<br>` +
        `contact@visamonde.com`
      );
    } else {
      // Template pour Inde eVisa (par défaut)
      setEmailSubjectECard(`${nom} ${prenom} - Dossier (${idDemande}) - Votre e-card est prête`);
      setEmailContentECard(
        `Bonjour ${prenom},<br><br>` +
        `Votre e-card vient d'être délivrée par les services consulaires, elle est disponible dans le lien ci-dessous.<br><br>` +
        `<div style="text-align: center; margin: 20px 0;">
          <a href="${urlECard}" style="background-color: #4CAF50; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold; font-size: 16px;">
            📄 Télécharger votre e-card
          </a>
        </div><br>` +
        `Afin de voyager en toute tranquillité, nous vous invitons à l'imprimer pour être en mesure de la présenter en douane.<br><br>` +
        `A savoir que votre e-card est accessible à tout moment dans votre espace client.<br><br>` +
        `Toute l'équipe de Visa Monde vous remercie pour votre confiance et vous souhaite un agréable voyage.<br>` +
        `Pour toute question, contactez-nous sur contact@visamonde.com<br><br>` +
        `Cordialement,<br>` +
        `L'équipe Visa Monde<br>` +
        `https://visamonde.com<br>` +
        `89 boulevard national 92250 LA GARENNE COLOMBES (FRANCE)<br>` +
        `(+33)1 86 22 08 06<br>` +
        `contact@visamonde.com`
      );
    }
    setSendEmailECard(true);
    setOpenModalECard(true);
  };

  const handleECardFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append("ecard", file);
    formData.append("sendEmail", sendEmailECard.toString());
    formData.append("subjectMail", emailSubjectECard);
    formData.append("mailContent", emailContentECard);

    try {
      // Upload du fichier e-card
      await uploadEcardLegrand(chemin, formData);

      alert('L\'e-card a été uploadée avec succès.');
      // Rafraîchir le lien de téléchargement
      const newUrl = await recupererEcardLegrand(chemin);
      setUrlEcard(newUrl);
      onValider();
    } catch (error) {
      console.error('Erreur lors de l\'upload de l\'e-card:', error);
      alert('Erreur lors de l\'upload de l\'e-card.');
    }
  };

  const handleModalSubmitECard = () => {
    setOpenModalECard(false);
    if (ecardInputRef.current) {
      ecardInputRef.current.click();
    }
  };

  // Helpers
  const selectText = (e: any) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      e.target.select();
    }
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    oublierMarque(name);
    const formattedValue = formatDate(value);

    setVoyageur((prev) => ({
      ...prev!,
      [name]: formattedValue,
    }));
  };

  const formatDate = (value: string) => {
    // Retire tous les caractères non numériques
    let onlyNumbers = value.replace(/[^\d]/g, '');
    // Ajoute automatiquement les `/`
    if (onlyNumbers.length > 2 && onlyNumbers.length <= 4) {
      onlyNumbers = onlyNumbers.slice(0, 2) + '/' + onlyNumbers.slice(2);
    } else if (onlyNumbers.length > 4) {
      onlyNumbers = onlyNumbers.slice(0, 2) + '/' + onlyNumbers.slice(2, 4) + '/' + onlyNumbers.slice(4, 8);
    }
    return onlyNumbers;
  };

  if (!voyageur) {
    return <p>Chargement des informations du voyageur...</p>;
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2} p={2}>
      <Box sx={{ mb: 2, p: 1, bgcolor: '#e3f2fd', borderRadius: 1, width: '100%', textAlign: 'center' }}>
        <strong>Service LEGRAND</strong> - Informations du voyageur
      </Box>

      <Box display="flex" alignItems="center" justifyContent="center" flexWrap="wrap" gap={2}>
        {urlPasseport && (
          <>
            <Link href={urlPasseport} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {passeportType === 'pdf' ? <PdfIcon color="error" /> : <ImageIcon color="primary" />}
              Voir le passeport
            </Link>
            <Chip
              label={passeportType === 'pdf' ? 'PDF' : 'Image'}
              size="small"
              color={passeportType === 'pdf' ? 'error' : 'primary'}
              variant="outlined"
            />
          </>
        )}
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => passeportInputRef.current?.click()}
          size="small"
        >
          Changer passeport
        </Button>
        {urlPasseport && userRole === 'superadmin' && (
          <Button
            variant="contained"
            size="small"
            startIcon={lectureEnCours ? <CircularProgress size={16} color="inherit" /> : <LirePasseportIcon />}
            onClick={handleLirePasseport}
            disabled={lectureEnCours}
          >
            {lectureEnCours ? 'Lecture du passeport…' : 'Lire le passeport'}
          </Button>
        )}
      </Box>

      {erreurLecture && (
        <Alert severity="error" onClose={() => setErreurLecture(null)} sx={{ width: '100%' }}>
          {erreurLecture}
        </Alert>
      )}
      {lecture && (
        <Alert
          severity={lecture.aVerifier ? 'warning' : 'success'}
          sx={{ width: '100%' }}
          action={
            <Button color="inherit" size="small" startIcon={<AnnulerIcon />} onClick={handleAnnulerLecture}>
              Annuler la lecture
            </Button>
          }
        >
          {lecture.resume}
          {lecture.avertissements.map((a) => (
            <Box key={a} sx={{ fontWeight: 600, mt: 0.5 }}>{a}</Box>
          ))}
        </Alert>
      )}

      {urlEcard && (
        <Box display="flex" alignItems="center" gap={2}>
          <Link href={urlEcard} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PdfIcon color="error" />
            Voir la carte d'arrivée
          </Link>
          <Chip label="PDF" size="small" color="error" variant="outlined" />
        </Box>
      )}

      <Box display="flex" alignItems="center" gap={2} width="100%">
        <Box position="relative">
          <Avatar
            src={photoUrl || (voyageur.url ? `${voyageur.url}?t=${photoTimestamp}` : undefined)}
            alt="Avatar"
            sx={{ width: 80, height: 80, cursor: 'pointer' }}
            onClick={() => window.open(photoUrl || voyageur.url, '_blank')}
          />
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              bottom: -5,
              right: -5,
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              padding: 0.5,
            }}
            onClick={() => photoInputRef.current?.click()}
          >
            <CameraIcon fontSize="small" />
          </IconButton>
        </Box>
        <TextField
          label="Nom"
          variant="outlined"
          name="nompax"
          {...marque('nompax')}
          fullWidth
          value={voyageur.nompax}
          onChange={handleChange}
          onMouseOver={selectText}
        />
      </Box>

      <TextField
        label="Prénom"
        variant="outlined"
        name="prenompax"
        {...marque('prenompax')}
        fullWidth
        value={voyageur.prenompax}
        onChange={handleChange}
        onMouseOver={selectText}
      />

      <TextField
        label="Numéro passeport"
        variant="outlined"
        name="numeropax"
        {...marque('numeropax')}
        fullWidth
        value={voyageur.numeropax}
        onChange={handleChange}
        onMouseOver={selectText}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Date de naissance"
        variant="outlined"
        name="datenaissance"
        {...marque('datenaissance')}
        fullWidth
        value={voyageur.datenaissance}
        onChange={handleDateChange}
        onMouseOver={selectText}
        InputLabelProps={{ shrink: true }}
      />

      {/* Sexe et ville de naissance : recopiés du passeport, repris par l'extension eVisa (Gender, Town/City of birth) */}
      <TextField
        select
        label="Sexe"
        variant="outlined"
        name="sexe"
        {...marque('sexe')}
        fullWidth
        value={voyageur.sexe || ''}
        onChange={handleChange}
        InputLabelProps={{ shrink: true }}
      >
        <MenuItem value="">Non renseigné</MenuItem>
        <MenuItem value="M">Masculin (M)</MenuItem>
        <MenuItem value="F">Féminin (F)</MenuItem>
        <MenuItem value="X">X</MenuItem>
      </TextField>

      <TextField
        label="Ville de naissance"
        variant="outlined"
        name="ville_naissance"
        {...marque('ville_naissance')}
        fullWidth
        value={voyageur.ville_naissance || ''}
        onChange={handleChange}
        onMouseOver={selectText}
        InputLabelProps={{ shrink: true }}
        inputProps={{ maxLength: 100 }}
      />

      <TextField
        label="Date de délivrance"
        variant="outlined"
        name="datedelivrance"
        {...marque('datedelivrance')}
        fullWidth
        value={voyageur.datedelivrance}
        onChange={handleDateChange}
        onMouseOver={selectText}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Date d'expiration"
        variant="outlined"
        name="dateexpiration"
        {...marque('dateexpiration')}
        fullWidth
        value={voyageur.dateexpiration}
        onChange={handleDateChange}
        onMouseOver={selectText}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Adresse"
        variant="outlined"
        name="adresse"
        {...marque('adresse')}
        fullWidth
        value={voyageur.adresse}
        onChange={handleChange}
        onMouseOver={selectText}
      />

      <TextField
        label="Code Postal"
        variant="outlined"
        name="cp"
        {...marque('cp')}
        fullWidth
        value={voyageur.cp}
        onChange={handleChange}
        onMouseOver={selectText}
      />

      <TextField
        label="Ville"
        variant="outlined"
        name="ville"
        {...marque('ville')}
        fullWidth
        value={voyageur.ville}
        onChange={handleChange}
        onMouseOver={selectText}
      />

      <TextField
        label="Profession"
        variant="outlined"
        name="profession"
        fullWidth
        value={voyageur.profession}
        onChange={handleChange}
        onMouseOver={selectText}
      />

      <TextField
        label="Téléphone"
        variant="outlined"
        name="telephone"
        fullWidth
        value={voyageur.telephone}
        onChange={handleChange}
        onMouseOver={selectText}
      />

      <TextField
        label="Etat Civil"
        variant="outlined"
        name="etat_civil"
        fullWidth
        value={voyageur.etat_civil}
        onChange={handleChange}
        onMouseOver={selectText}
      />

      {/* Bouton Valider les modifications - Visible uniquement pour superadmin */}
      {userRole === 'superadmin' && (
        <Button onClick={handleSubmit} variant="contained" color="primary" sx={{ mt: 2 }}>
          Valider les modifications
        </Button>
      )}

      {/* Input files cachés */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept=".pdf"
      />

      <input
        type="file"
        ref={passeportInputRef}
        onChange={handlePasseportUpload}
        style={{ display: 'none' }}
        accept=".pdf,.jpg,.jpeg,.png"
      />

      <input
        type="file"
        ref={photoInputRef}
        onChange={handlePhotoUpload}
        style={{ display: 'none' }}
        accept=".jpg,.jpeg,.png"
      />

      <input
        type="file"
        ref={ecardInputRef}
        onChange={handleECardFileChange}
        style={{ display: 'none' }}
        accept=".pdf"
      />

      {/* Boutons visa */}
      {/* Boutons Upload Visa et Mail - Visible uniquement pour superadmin */}
      {userRole === 'superadmin' && (
        <>
          {isEVisa ? (
            <>
              <Button variant="contained" onClick={handleUploadClick} sx={{ mt: 2 }}>
                Uploader Visa (eVisa)
              </Button>
              {(isIndeEVisa || isTogoEVisa || isECardPays) && (
                <Button variant="contained" color="warning" onClick={handleUploadECardClick} sx={{ mt: 2, ml: 2 }}>
                  Upload E-card
                </Button>
              )}
            </>
          ) : isECardPays ? (
            <Button variant="contained" color="warning" onClick={handleUploadECardClick} sx={{ mt: 2 }}>
              Upload E-card
            </Button>
          ) : (
            <>
              <Button variant="contained" color="secondary" onClick={handleVisaClassiqueClick} sx={{ mt: 2 }}>
                Envoyer mail visa traité LEGRAND
              </Button>
              {isChine && chineWantsECard && (
                <Button variant="contained" color="warning" onClick={handleUploadECardClick} sx={{ mt: 2, ml: 2 }}>
                  Upload E-card
                </Button>
              )}
            </>
          )}
        </>
      )}

      {/* Modale pour l'eVisa */}
      <Dialog open={openModalEVisa} onClose={() => setOpenModalEVisa(false)} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>Informations Visa (eVisa) - LEGRAND</DialogTitle>
        <DialogContent>
          <TextField
            label="Date de validité"
            type="date"
            fullWidth
            value={dateValiditeEVisa}
            onChange={(e) => setDateValiditeEVisa(e.target.value)}
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Date d'expiration"
            type="date"
            fullWidth
            value={dateExpirationEVisa}
            onChange={(e) => setDateExpirationEVisa(e.target.value)}
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Informations Visa"
            variant="outlined"
            fullWidth
            value={infosVisaEVisa}
            onChange={(e) => setInfosVisaEVisa(e.target.value)}
            sx={{ mt: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmailEVisa}
                onChange={(e) => setSendEmailEVisa(e.target.checked)}
              />
            }
            label="Envoyer un email au client"
            sx={{ mt: 2 }}
          />
          {sendEmailEVisa && (
            <>
              <TextField
                label="Objet du mail"
                fullWidth
                value={emailSubjectEVisa}
                onChange={(e) => setEmailSubjectEVisa(e.target.value)}
                sx={{ mt: 2 }}
              />
              <TextField
                label="Contenu du mail"
                multiline
                rows={12}
                fullWidth
                value={emailContentEVisa}
                onChange={(e) => setEmailContentEVisa(e.target.value)}
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModalEVisa(false)}>Annuler</Button>
          <Button onClick={handleModalSubmitEVisa} variant="contained" color="primary">
            Valider
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modale pour visa classique */}
      <Dialog open={openModalVisaClassique} onClose={() => setOpenModalVisaClassique(false)} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>Mail Visa Traité - Service LEGRAND</DialogTitle>
        <DialogContent>
          <TextField
            label="Date de validité"
            type="date"
            fullWidth
            value={dateValiditeVC}
            onChange={(e) => setDateValiditeVC(e.target.value)}
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Date d'expiration"
            type="date"
            fullWidth
            value={dateExpirationVC}
            onChange={(e) => setDateExpirationVC(e.target.value)}
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Informations Visa"
            variant="outlined"
            fullWidth
            multiline
            rows={3}
            value={infosVisaVC}
            onChange={(e) => setInfosVisaVC(e.target.value)}
            sx={{ mt: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmailVC}
                onChange={(e) => setSendEmailVC(e.target.checked)}
              />
            }
            label="Envoyer un email au client"
            sx={{ mt: 2 }}
          />
          {sendEmailVC && (
            <>
              <TextField
                label="Objet du mail"
                variant="outlined"
                fullWidth
                value={emailSubjectVC}
                onChange={(e) => setEmailSubjectVC(e.target.value)}
                sx={{ mt: 2 }}
              />
              <TextField
                label="Contenu du mail"
                variant="outlined"
                fullWidth
                multiline
                rows={12}
                value={emailContentVC}
                onChange={(e) => setEmailContentVC(e.target.value)}
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModalVisaClassique(false)}>Annuler</Button>
          <Button onClick={handleModalSubmitVisaClassique} variant="contained" color="primary">
            {sendEmailVC ? 'Envoyer' : 'Valider'}
          </Button>
        </DialogActions>
      </Dialog>

      {/*
        Modale pour l'e-card Inde ou Togo => personnalisation du mail
      */}
      <Dialog open={openModalECard} onClose={() => setOpenModalECard(false)} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>Upload E-Card {isTogoEVisa ? '(Togo eVisa)' : isECardPays ? '(Carte d\'arrivée)' : (isChine && chineWantsECard) ? '(Carte d\'arrivée Chine)' : '(Inde eVisa)'} - LEGRAND</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmailECard}
                onChange={(e) => setSendEmailECard(e.target.checked)}
              />
            }
            label="Envoyer un email au client"
            sx={{ mt: 2 }}
          />
          {sendEmailECard && (
            <>
              <TextField
                label="Objet de l'email"
                variant="outlined"
                fullWidth
                value={emailSubjectECard}
                onChange={(e) => setEmailSubjectECard(e.target.value)}
                sx={{ mt: 2 }}
              />
              <TextField
                label="Contenu de l'email"
                variant="outlined"
                fullWidth
                multiline
                rows={10}
                value={emailContentECard}
                onChange={(e) => setEmailContentECard(e.target.value)}
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModalECard(false)}>Annuler</Button>
          <Button onClick={handleModalSubmitECard} variant="contained" color="warning">
            Valider et Uploader E-Card
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InfoVoyageurLegrand;